// import { Constants } from '../../constants';
import { Initializer } from './initializer';

export class Sidebar {
    private sidebar: HTMLDivElement;
    private sidebarWidth: number = 0;
    private init: Initializer;
    private gallery: ImagePreviewerInstance | null = null;
    private modalSubmitListenerAdded: Boolean = false;
    private config = window.MapEngineConfig;

    constructor(querySelector: string) {
        this.sidebar = document.querySelector(querySelector) as HTMLDivElement;
        this.init = new Initializer();
    }

    public getWidth(): number {
        if (this.sidebarWidth === 0) {
            this.sidebarWidth = this.sidebar.clientWidth;
        }
        return this.sidebarWidth;
    }

    public toggle(): void {
        const wasOpen = this.isOpen();
        this.sidebar.classList.toggle('open');
        this.dispatchEvent('toggle', { open: this.isOpen(), wasOpen: wasOpen });
    }

    public open(): void {
        const wasOpen = this.isOpen();
        this.sidebar.classList.toggle('open', true);
        this.dispatchEvent('open');
        this.dispatchEvent('toggle', { open: true, wasOpen: wasOpen });
    }

    public close(): void {
        const wasOpen = this.isOpen();
        this.sidebar.classList.toggle('open', false);
        this.dispatchEvent('close');
        this.dispatchEvent('toggle', { open: false, wasOpen: wasOpen });
    }

    public isOpen(): boolean {
        return this.sidebar.classList.contains('open');
    }

    public addToggleHandler(querySelector: string): void {
        const toggleButton: HTMLElement = document.querySelector(querySelector) as HTMLElement;
        toggleButton.addEventListener('click', () => this.toggle());
    }

    public addOpenHandler(querySelector: string): void {
        const openButton: HTMLElement = document.querySelector(querySelector) as HTMLElement;
        openButton.addEventListener('click', () => this.open());
    }

    public addCloseHandler(querySelector: string): void {
        const closeButton: HTMLElement = document.querySelector(querySelector) as HTMLElement;
        closeButton.addEventListener('click', () => this.close());
    }

    public addToggleHandlerToAll(querySelector: string): void {
        const toggleButtons: NodeListOf<HTMLElement> = document.querySelectorAll(querySelector);
        toggleButtons.forEach((button: HTMLElement) => {
            button.addEventListener('click', () => this.toggle());
        });
    }

    public addOpenHandlerToAll(querySelector: string): void {
        const openButtons: NodeListOf<HTMLElement> = document.querySelectorAll(querySelector);
        openButtons.forEach((button: HTMLElement) => {
            button.addEventListener('click', () => this.open());
        });
    }

    public addCloseHandlerToAll(querySelector: string): void {
        const closeButtons: NodeListOf<HTMLElement> = document.querySelectorAll(querySelector);
        closeButtons.forEach((button: HTMLElement) => {
            button.addEventListener('click', () => this.close());
        });
    }

    public getElement(): HTMLDivElement {
        return this.sidebar;
    }

    public fillWithHtml(html: string, prepareSidebar: boolean = true): void {
        this.sidebar.innerHTML = html;
        if(prepareSidebar) this.prepareSidebar();
        
    }

    public prepareSidebar(): void {
        this.decryptParts(this.sidebar);
        this.initGallery(this.sidebar);
        this.attachEventListeners();
        this.init.chart(this);
    }

    private attachEventListeners(): void {
        this.addCloseHandlerToAll('.sidebar-nav');
    }

    public decryptParts(container: HTMLDivElement = this.sidebar): void {
        const elements = container.querySelectorAll('.crypt-element');
        elements.forEach((element: Element) => {
            try {
                const textCrypt = element.getAttribute('data-crypt-text');
                const scheme = element.getAttribute('data-href-scheme');
                const urlCrypt = element.getAttribute('data-crypt-url');

                // Only decrypt if we have non-empty values and a valid key
                let url = null;
                let text = null;

                if (urlCrypt && urlCrypt.trim() !== '' && this.config.cryptojs?.key) {
                    try {
                        url = CryptoJSAesJson.decrypt(urlCrypt, this.config.cryptojs.key);
                    } catch (decryptError) {
                        console.warn("Failed to decrypt URL:", decryptError);
                    }
                }

                if (textCrypt && textCrypt.trim() !== '' && this.config.cryptojs?.key) {
                    try {
                        text = CryptoJSAesJson.decrypt(textCrypt, this.config.cryptojs.key);
                    } catch (decryptError) {
                        console.warn("Failed to decrypt text:", decryptError);
                    }
                }

                if (element.classList.contains('ico-button')) {
                    const textElement = element.querySelector('.text');
                    if (textElement) textElement.textContent = text || url || '';
                } else {
                    element.textContent = text || url || '';
                }

                if (scheme && text) {
                    element.setAttribute('href', scheme + ':' + text);
                } else if (url || text) {
                    element.setAttribute('href', url || text || '');
                }

                element.removeAttribute('data-crypt-text');
                element.removeAttribute('data-crypt-url');
                element.removeAttribute('data-href-scheme');
            } catch (e) {
                console.error("Error in decryption process", e);
            }
        });
    }

    public initGallery(container: HTMLDivElement = this.sidebar): void {
        this.gallery = new ImagePreviewer('#sidebar', {
            zoom: {
                min: 2,
                max: 20,
                step: 0.3,
            },
            scrollbar: false,
        });
    }

    public fillWithSpinner(): void {
        this.fillWithHtml('<section class="spinner"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Lädt...</span></div></section>', false);
    }

    public fillWithHtmlFromUrl(url: string): void {
        this.fillWithSpinner();
        fetch(url)
            .then(response => response.text())
            .then(html => this.fillWithHtml(html));
    }

    public addEventListener(eventName: string, callback: (event: CustomEvent) => void) {
        this.sidebar.addEventListener(eventName, (event: Event) => {
            callback(event as CustomEvent);
        });
    }

    public removeEventListener(eventName: string, callback: (event: CustomEvent) => void) {
        this.sidebar.removeEventListener(eventName, (event: Event) => {
            callback(event as CustomEvent);
        });
    }

    public dispatchEvent(eventName: string, data: any = {}): void {
        const event = new CustomEvent(eventName, { detail: data });
        this.sidebar.dispatchEvent(event);
    }
}